import { createContext, useContext, useEffect, useState } from "react";
import { getMe } from "../services/userService";
import AuthService from "../auth/AuthService";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Guard: no valid session — treat as guest
    if (!AuthService.hasValidSession()) {
      setUser(null);
      setLoading(false);
      return;
    }

    const auth = AuthService.getAuth();

    // Pre-set user from localStorage so the app stays logged in during the fetch
    if (auth?.phone) {
      setUser({
        phone: auth.phone,
        role: auth.role || "USER",
        name: auth.name || auth.displayName || auth.fullName || "",
        pendingOnboardingId: auth.pendingOnboardingId || null
      });
    }

    getMe()
      .then((data) => {
        const payload = data?.data ?? data;
        const resolved = payload?.user ?? payload;
        
        if (resolved && Object.keys(resolved).length > 0 && resolved.phone) {
          setUser(resolved);
        } else if (auth?.phone) {
          // Fallback to local storage if server returned empty object but token valid
          setUser({
            phone: auth.phone,
            role: auth.role || "USER",
            name: auth.name || auth.displayName || auth.fullName || ""
          });
        } else {
          setUser(null);
        }
        
        // Refresh token logic
        const freshToken = data?.token || payload?.token;
        if (freshToken && auth) {
          if (auth.token !== freshToken) {
            auth.token = freshToken;
            AuthService.saveAuth({ token: freshToken, user: resolved || auth });
            console.log("[AuthContext] Refreshed JWT token from /users/me");
          }
        }
      })
      .catch((err) => {
        // Network failure fallback: use localStorage data so the user isn't
        // unexpectedly logged out due to a transient connectivity issue.
        console.warn("getMe() failed, using cached auth:", err.message);
        if (auth?.phone) {
          setUser({
            phone: auth.phone,
            role: auth.role || "USER",
            name: auth.name || auth.displayName || auth.fullName || ""
          });
        } else {
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  async function refresh() {
    const auth = AuthService.getAuth();
    if (auth?.phone && !user) {
      setUser({
        phone: auth.phone,
        role: auth.role || "USER",
        name: auth.name || ""
      });
    }
    try {
      const data = await getMe();
      const payload = data?.data ?? data;
      const resolved = payload?.user ?? payload;
      setUser(resolved);
      if (auth) {
        auth.name = resolved?.name || resolved?.displayName || auth.name;
        AuthService.saveAuth({ token: auth.token, user: resolved || auth });
      }
    } catch (e) {
      console.error("AuthContext refresh failed:", e);
      if (auth?.phone) {
        setUser({
          phone: auth.phone,
          role: auth.role || "USER",
          name: auth.name || ""
        });
      }
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
